import { createApp } from 'vue'
import WaveSurfer from "wavesurfer.js";
import { getTimeForSeconds } from "../helpers";

document.addEventListener("turbo:load", () => {
  const el = document.getElementById("vue-reply");
  if (!el) return;

  const payload = JSON.parse(el.dataset.payload);
  const app = createApp({
    data () {
      return {
        activeMode: "voice",
        voiceState: "ready",
        playState: "pause",
        recording: {},
        timer: {
          interval: null,
          startTimestamp: null,
        },
        time: "0:00",
        playtime: "0:00",
        duration: "0:00",
        wavesurfer: null,
        mediaStream: null,
        recordedChunks: [],
        ...payload,
      }
    },
    methods: {
      async onRecord() {
        try {
          if (!navigator.mediaDevices || !MediaRecorder) {
            throw new Error('MediaRecorder not supported by this browser');
          }

          // Optimierte Audio-Constraints für Voice/VOIP
          const audioConstraints = {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 16000,        // Reduzierte Sample-Rate für Voice
              channelCount: 1           // Mono für kleinere Dateien
            }
          };

          this.mediaStream = await navigator.mediaDevices.getUserMedia(audioConstraints);
          
          // Chunks zurücksetzen
          this.recordedChunks = [];
          
          // Einheitliches Format: MP4 mit AAC für maximale Browser-Kompatibilität
          const config = {
            mimeType: 'audio/mp4',
            audioBitsPerSecond: 32000  // 32kbps für gute Voice-Qualität bei kleiner Dateigröße
          };
          
          // Fallback für ältere Browser
          if (!MediaRecorder.isTypeSupported(config.mimeType)) {
            throw new Error('MP4-Audio is not supported by this browser');
          }
          
          this.recorder = new MediaRecorder(this.mediaStream, config);
          
          // Event-Handler
          this.recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              this.recordedChunks.push(event.data);
            }
          };
          
          this.recorder.onstop = () => {
            this.processRecording();
          };
          
          this.recorder.onerror = (event) => {
            console.error('Recorder Error:', event.error);
          };
          
          // Ohne timeslice starten - Browser sammelt automatisch
          this.recorder.start();

          this.timer = {
            interval: setInterval(() => {
              const differenceInMs = new Date().getTime() - this.timer.startTimestamp;
              const totalSeconds = Math.floor(differenceInMs / 1000);
              this.time = getTimeForSeconds(totalSeconds);
            }, 1000),
            startTimestamp: new Date().getTime(),
          };

          this.voiceState = "recording";
          
        } catch (error) {
          console.error('Error while starting to record:', error);
        }
      },

      processRecording() {
        if (this.recordedChunks.length === 0) {
          console.error('No recording data available. Please try again.');
          return;
        }
        
        const time = new Date().getTime();
        const mimeType = this.recorder.mimeType;
        
        const dataBlob = new Blob(this.recordedChunks, { type: mimeType });
        
        this.recording = {
          url: URL.createObjectURL(dataBlob),
          // MP4-Dateiendung für AAC-Audio
          file: new File([dataBlob], `${time}.mp4`, {
            type: mimeType,
            lastModified: time,
          }),
        };
        
        // WaveSurfer nach kurzer Verzögerung initialisieren
        setTimeout(() => {
          this.initializeWaveSurfer();
        }, 300);
      },

      resetTimer() {
        clearInterval(this.timer.interval);
        this.timer.startTimestamp = null;
        this.time = "0:00";
      },

      async onStop() {
        if (this.recorder && this.recorder.state === 'recording') {
          this.recorder.stop();
        }
        this.resetTimer();
        this.voiceState = "recorded";
      },

      async onReset() {
        if (this.recorder) {
          if (this.recorder.state === 'recording') {
            this.recorder.stop();
          }
          this.recorder = null;
        }
        
        if (this.mediaStream) {
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
        }

        this.resetTimer();

        if (this.wavesurfer) {
          this.wavesurfer.destroy();
          this.wavesurfer = null;
        }

        if (this.recording.url) {
          URL.revokeObjectURL(this.recording.url);
        }

        this.recording = {};
        this.recordedChunks = [];
        this.voiceState = "ready";
      },

      onPlay() {
        if (this.wavesurfer) {
          this.wavesurfer.play();
        }
      },

      onPause() {
        if (this.wavesurfer) {
          this.wavesurfer.pause();
        }
      },

      onSubmit() {
        if (
          this.voiceAnswersEnabled &&
          this.activeMode === "voice" &&
          this.recording.file
        ) {
          const container = new DataTransfer();
          container.items.add(this.recording.file);
          this.$refs.voiceInput.files = container.files;
        }

        this.$refs.recaptchaData.value =
          document.getElementById("g-recaptcha-response").value ||
          document.getElementById("g-recaptcha-response-data-answer").value;

        return true;
      },

      initializeWaveSurfer() {
        if (!this.wavesurfer && this.$refs.wave && this.recording.url) {
          let playtimeInterval;
          
          try {
            this.wavesurfer = WaveSurfer.create({
              container: this.$refs.wave,
              barWidth: 2,
              barHeight: 2,
              barGap: null,
              cursorWidth: 1,
              height: 64,
              normalize: true,
              progressColor: "#589ae8",
              waveColor: "#727272",
            });

            this.wavesurfer.on("finish", () => {
              this.playState = "pause";
              clearInterval(playtimeInterval);
            });
            
            this.wavesurfer.on("pause", () => {
              this.playState = "pause";
              clearInterval(playtimeInterval);
            });
            
            this.wavesurfer.on("play", () => {
              this.playState = "playing";
              playtimeInterval = setInterval(() => {
                this.playtime = getTimeForSeconds(
                  Math.floor(this.wavesurfer.getCurrentTime())
                );
              }, 100);
            });
            
            this.wavesurfer.on("ready", () => {
              this.duration = getTimeForSeconds(
                Math.floor(this.wavesurfer.getDuration())
              );
            });
            
            this.wavesurfer.on("error", (error) => {
              console.error('WaveSurfer error:', error);
            });
            
            this.wavesurfer.on("seek", () => this.wavesurfer.play());
            
            this.wavesurfer.load(this.recording.url);
            
          } catch (error) {
            console.error('Error while loading WaveSurfer:', error);
          }
        }
      }
    }
  });
  app.mount(el)
});